import { useCallback, useEffect, useRef, useState } from "react";
import { Input, Row, Table } from "reactstrap";
import swal from "sweetalert";
import { LoadingSpinner } from "../../../constants/const";
import { GetLanguageString } from "../../helper/Components";
import { GetSelectedLanguage } from "../../helper/Method";
import { ApiLFC } from "../../letterForCounselling/ApiLFC";

const LetterForCounsellingDataTable = ({
  questionsList,
  setQuestionsList,
  setErrorMessage,
  errorMessage,
  letterColor,
  calledAs,
}) => {
  const [focusedRow, setFocusedRow] = useState(0);
  const selectedLanguage = GetSelectedLanguage();
  const [loading, setLoading] = useState(false);
  const [ratingsList, setRatingsList] = useState([]);

  const questionsRef = useRef(questionsList);
  const focusedRowRef = useRef(focusedRow);

  useEffect(() => {
    setLoading(true);

    ApiLFC()
      .GetLetterForCounselingRatingsList()
      .then((res) => setRatingsList(res.data))
      .catch((err) =>
        swal({
          title: err?.message || "Something went wrong",
          icon: "error",
          buttons: "OK",
        }),
      )
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    questionsRef.current = questionsList;
  }, [questionsList]);

  useEffect(() => {
    focusedRowRef.current = focusedRow;
  }, [focusedRow]);

  const handleRating = useCallback(
    (index, value) => {
      const updatedQuestions = [...questionsRef.current];
      updatedQuestions[index] = {
        ...updatedQuestions[index],
        rating: value,
        errorColor: "#ffffff",
      };

      if (updatedQuestions.every((q) => q.rating !== 0)) {
        setErrorMessage((prev) => ({ ...prev, questions: "" }));
      }

      setQuestionsList(updatedQuestions);
    },
    [setErrorMessage, setQuestionsList],
  );

  useEffect(() => {
    if (calledAs === "view" || calledAs === "lfc") return;

    setQuestionsList((prevQuestions) => {
      const updated = [...prevQuestions];
      if (!updated[0]?.rating && calledAs === "add") {
        updated[0].rating = 1;
      }
      return updated;
    });

    const handleKeyDown = (event) => {
      const updatedQuestions = [...questionsRef.current];
      const currentRow = focusedRowRef.current;

      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        const delta = event.key === "ArrowUp" ? -1 : 1;
        const newRow = Math.max(
          0,
          Math.min(updatedQuestions.length - 1, currentRow + delta),
        );

        if (!updatedQuestions[newRow].rating) {
          updatedQuestions[newRow].rating = 1;
          updatedQuestions[newRow].errorColor = "#ffffff";
        }

        if (newRow !== currentRow) {
          setQuestionsList(updatedQuestions);
          setFocusedRow(newRow);
        }
      }

      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        const delta =
          (event.key === "ArrowLeft" ? -1 : 1) *
          (selectedLanguage === "ur" ? -1 : 1);
        const currentRating = updatedQuestions[currentRow]?.rating || 1;
        const newRating = Math.max(
          1,
          Math.min(
            updatedQuestions[currentRow]?.ratings.length,
            currentRating + delta,
          ),
        );
        handleRating(currentRow, newRating);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [calledAs, handleRating, setQuestionsList, selectedLanguage, ratingsList]);

  const renderRatingCell = (index, item, ratingValue) => {
    const isDisabled = !item.ratings?.includes(ratingValue);

    return (
      <td
        key={ratingValue}
        className="text-center"
        onClick={() =>
          calledAs === "view" || calledAs === "lfc"
            ? null
            : handleRating(index, ratingValue)
        }
        style={{
          cursor:
            calledAs === "view" || calledAs === "lfc"
              ? "default"
              : isDisabled
                ? "not-allowed"
                : "pointer",
          backgroundColor:
             focusedRow === index && calledAs !== "view" && calledAs !== "lfc"
            ? "#eaeaea"
              : isDisabled
                ? "#f5f5f5"
                : "transparent",
          opacity: isDisabled ? 0.6 : 1,
        }}
      >
        <Input
          type="radio"
          checked={item.rating === ratingValue}
          disabled={isDisabled}
          readOnly
          style={{
            pointerEvents: "none",
            transform: "scale(1.2)",
            marginRight: "10px",
            cursor: isDisabled ? "not-allowed" : "pointer",
            backgroundColor:
              item.rating === ratingValue
                ? letterColor
                : isDisabled
                  ? "#dcdcdc"
                  : "transparent",
            border: `2px solid ${isDisabled ? "#b5b5b5" : letterColor}`,
          }}
        />
      </td>
    );
  };

  return (
    <>
      {!loading ? (
        <>
          <Table responsive bordered>
            <thead>
              <tr>
                <th
                  style={{ backgroundColor: letterColor }}
                  className="text-center"
                >
                  <GetLanguageString props="settings_letter_for_counselling_question_q_number" />
                </th>
                <th style={{ backgroundColor: letterColor }}>
                  <GetLanguageString props="settings_letter_for_counselling_questions" />
                </th>
                {ratingsList.map((r) => (
                  <th
                    key={r.value}
                    style={{ backgroundColor: letterColor }}
                    className="text-center ur"
                  >
                    {r.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {questionsList.map((item, index) => (
                <tr
                  key={index}
                  style={{
                    backgroundColor: item.errorColor,
                    outline:
                      focusedRow === index &&
                      calledAs !== "view" &&
                      calledAs !== "lfc"
                        ? "1px solid " + letterColor
                        : "none",
                  }}
                >
                  <td className="text-center" style={{ fontSize: 15 }}>
                    {item.questionNumber}
                  </td>
                  <td className="ur" style={{ fontSize: 17 }}>
                    {item.questionTitle}
                  </td>
                  {ratingsList.map((r) =>
                    renderRatingCell(index, item, r.value),
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
          {Object.values(errorMessage).map(
            (msg, i) =>
              msg && (
                <Row key={i}>
                  <h5 className="text-error">
                    * {<GetLanguageString props={msg} />}
                  </h5>
                </Row>
              ),
          )}
        </>
      ) : (
        LoadingSpinner()
      )}
    </>
  );
};

export default LetterForCounsellingDataTable;
